/**
 * Leaves on a sphere, ancestors inside it, and every reference routed as a bundle along the
 * hierarchy between them.
 *
 * This is Holten's hierarchical edge bundling with the containment circle replaced by a sphere. The
 * reason to want it is the case the other layouts handle worst: a few hundred references drawn as
 * straight lines through a ball of nodes is a solid mass with no readable structure, whereas the
 * same references bundled show which parts of the estate talk to which as thick ropes between
 * regions. What it costs is the individual edge - a bundle tells you two subtrees are coupled, not
 * which two components.
 *
 * Leaves are placed in depth-first order, so siblings land next to each other and a subtree occupies
 * a contiguous run. Without that ordering the bundles have nothing to bundle toward: an ancestor's
 * descendants would be scattered over the whole sphere and every route through it would leave in a
 * different direction.
 *
 * A contiguous run is not a contiguous *region*, which is the trap here. One golden-angle spiral
 * over every leaf distributes them evenly - that is what it is for - by turning roughly 137 degrees
 * of longitude per step, so a run of leaves comes out as a belt at one latitude wrapped around every
 * longitude rather than as a patch. A belt's mean direction is its axis, so every top-level
 * subtree's ancestor resolved to nearly the same point near the center and the bundles left it as a
 * starburst. `sectorRootIndices` is the answer: a sector per top-level subtree, sized by its share
 * of the leaves, spiralled within its own wedge of longitude.
 */

import type { Hierarchy } from '../graph/topology';
import type { Vec3 } from '../types';

export type BundledSphereArgs = {
  hierarchy: Hierarchy;
  nodeCount: number;
  /** Radius the leaves sit at. Ancestors are placed inside it by depth. */
  radius: number;
  /**
   * Radius the shallowest nodes sit at, as a fraction of `radius`.
   *
   * Not 0. A root at the center puts every bundle in the estate through one point, which is a
   * hairball with extra steps - the thing the layout exists to avoid. Holding the roots out at a
   * fraction of the radius gives each one its own region for its bundles to pass through.
   */
  innerRadiusFraction: number;
  /**
   * Roots that each get their own wedge of the sphere, or `null` for one spiral over every leaf.
   *
   * For the handful of groups a type grouping produces, not for a root list of any length: a wedge
   * per root over a flat estate is one wedge per component, and a wedge holding a single leaf puts
   * it on the equator - which lines the whole estate up around one circle.
   *
   * Roots outside the list still get a wedge, appended after these. Nothing else would place their
   * leaves, and a leaf with no position is drawn at the origin.
   */
  sectorRootIndices: readonly number[] | null;
};

/** Depth-first order over the forest, parents before children, roots in index order. */
const toDepthFirstOrder = ({
  childIndices,
  rootIndices,
}: Hierarchy): number[] => {
  const order: number[] = [];
  const stack = [...rootIndices].reverse();
  const seen = new Set<number>();

  while (stack.length > 0) {
    const index = stack.pop();
    if (index === undefined || seen.has(index)) continue;
    seen.add(index);
    order.push(index);

    const children = childIndices[index];
    for (let at = children.length - 1; at >= 0; at--) {
      stack.push(children[at]);
    }
  }

  return order;
};

/** Turn per step of a Fibonacci spiral, which is what spreads it rather than coiling it. */
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

/**
 * The most of the radius one level of the hierarchy sits inside its children.
 *
 * A step inward from the rim, capped, rather than a ramp spread over however many levels there
 * happen to be. Under a ramp the depth a bundle dives to is a function of the estate's depth
 * instead of how far up the hierarchy the bundle goes, so the two-level case - which is what a type
 * grouping over a flat viewpoint is - put its only interior level at the ramp's floor. Every route
 * then dived to the same depth near the center and the bundles came out as one knot rather than as
 * cords between regions.
 *
 * Only shallow hierarchies notice. `toLevelStep` takes whichever step is smaller, so any hierarchy
 * deep enough that the ramp's own step was already under this keeps exactly the radii it had.
 */
const MAXIMUM_LEVEL_STEP = 0.25;

const toLevelStep = (maxDepth: number, innerRadiusFraction: number): number =>
  maxDepth === 0
    ? 0
    : Math.min(MAXIMUM_LEVEL_STEP, (1 - innerRadiusFraction) / maxDepth);

/**
 * Golden-ratio conjugate, for spreading longitudes *within* a wedge.
 *
 * Taken modulo one and scaled to the wedge rather than added as an absolute angle: a wedge is
 * narrower than a full turn, so stepping by `GOLDEN_ANGLE` inside one would leave it immediately.
 * The sequence is low-discrepancy on the unit interval, so consecutive leaves still land apart and
 * the wedge fills evenly instead of coiling.
 */
const GOLDEN_FRACTION = (Math.sqrt(5) - 1) / 2;

/**
 * Leaves under each root, depth-first, every leaf in exactly one sector.
 *
 * The `seen` set spans the sectors rather than sitting inside one, so a node a cycle made reachable
 * from two roots is placed once - being placed twice means the second sector's arithmetic silently
 * moves a leaf the first one already positioned.
 */
const toSectorLeaves = (
  { childIndices }: Hierarchy,
  roots: readonly number[]
): number[][] => {
  const seen = new Set<number>();

  return roots.map(root => {
    const leaves: number[] = [];
    const stack = [root];

    while (stack.length > 0) {
      const index = stack.pop();
      if (index === undefined || seen.has(index)) continue;
      seen.add(index);

      const children = childIndices[index];
      if (children.length === 0) {
        leaves.push(index);
        continue;
      }
      for (let at = children.length - 1; at >= 0; at--) {
        stack.push(children[at]);
      }
    }

    return leaves;
  });
};

/**
 * Evenly over the whole sphere, in one spiral.
 *
 * Height uniform in [-1, 1] because equal slabs of height are equal areas of a sphere - which is
 * why this distributes evenly where an even step of latitude would crowd the poles.
 */
const placeGlobalSpiral = (
  leaves: readonly number[],
  radius: number,
  positions: Vec3[]
): void => {
  leaves.forEach((index, at) => {
    const height = leaves.length === 1 ? 0 : 1 - (2 * at) / (leaves.length - 1);
    const ring = Math.sqrt(Math.max(0, 1 - height * height));
    const angle = at * GOLDEN_ANGLE;

    positions[index] = [
      Math.cos(angle) * ring * radius,
      Math.sin(angle) * ring * radius,
      height * radius,
    ];
  });
};

/**
 * One sector's leaves inside its own wedge of longitude, pole to pole.
 *
 * A wedge rather than a cap, because wedges tile the sphere exactly: area goes with the angle, so
 * sizing each by its share of the leaves fills the surface with no gap and no overlap at one
 * density. Caps proportioned the same way would do neither, and the seams would read as structure.
 *
 * Offset by half a step so a wedge holding one leaf puts it at the equator of its own wedge rather
 * than at a pole every other sector shares.
 */
const placeSector = (
  leaves: readonly number[],
  fromAngle: number,
  toAngle: number,
  radius: number,
  positions: Vec3[]
): void => {
  leaves.forEach((index, at) => {
    const height = 1 - (2 * (at + 0.5)) / leaves.length;
    const ring = Math.sqrt(Math.max(0, 1 - height * height));
    const angle =
      fromAngle + (toAngle - fromAngle) * ((at * GOLDEN_FRACTION) % 1);

    positions[index] = [
      Math.cos(angle) * ring * radius,
      Math.sin(angle) * ring * radius,
      height * radius,
    ];
  });
};

export const toBundledSpherePositions = ({
  hierarchy,
  nodeCount,
  radius,
  innerRadiusFraction,
  sectorRootIndices,
}: BundledSphereArgs): Vec3[] => {
  const { childIndices, maxDepth, depths, rootIndices } = hierarchy;
  const order = toDepthFirstOrder(hierarchy);
  const positions = new Array<Vec3>(nodeCount).fill([0, 0, 0]);
  const levelStep = toLevelStep(maxDepth, innerRadiusFraction);

  if (sectorRootIndices === null) {
    placeGlobalSpiral(
      order.filter(index => childIndices[index].length === 0),
      radius,
      positions
    );
  } else {
    const named = new Set(sectorRootIndices);
    const sectorRoots = [
      ...sectorRootIndices,
      ...rootIndices.filter(index => !named.has(index)),
    ];
    const sectors = toSectorLeaves(hierarchy, sectorRoots);
    const leafTotal =
      sectors.reduce((total, leaves) => total + leaves.length, 0) || 1;

    let fromAngle = 0;
    for (const leaves of sectors) {
      const span = (leaves.length / leafTotal) * Math.PI * 2;
      placeSector(leaves, fromAngle, fromAngle + span, radius, positions);
      fromAngle += span;
    }
  }

  /**
   * An ancestor sits in the mean direction of its children, pulled inward by its depth.
   *
   * The mean of the children's positions rather than a direction chosen for it, so a bundle leaving
   * a subtree leaves from where the subtree actually is. Normalized and rescaled rather than used
   * as-is: the mean of points spread over a spherical cap lands well inside the sphere, and the
   * radius is what encodes depth here, so letting the averaging set it would collapse the levels.
   *
   * Walked in reverse depth-first order, which visits every child before its parent.
   */
  for (let at = order.length - 1; at >= 0; at--) {
    const index = order[at];
    const children = childIndices[index];
    if (children.length === 0) continue;

    const sum = children.reduce(
      (total, child) => [
        total[0] + positions[child][0],
        total[1] + positions[child][1],
        total[2] + positions[child][2],
      ],
      [0, 0, 0]
    );
    const length = Math.hypot(sum[0], sum[1], sum[2]) || 1;
    const target = radius * (1 - levelStep * (maxDepth - depths[index]));

    positions[index] = [
      (sum[0] / length) * target,
      (sum[1] / length) * target,
      (sum[2] / length) * target,
    ];
  }

  return positions;
};

/**
 * Three great circles, as a cage.
 *
 * A point cloud on a sphere does not read as a sphere from a still image - the silhouette is a disc
 * and the depth cue is only the shading. Three orthogonal rings give the eye the surface, so a node
 * near the rim reads as facing away rather than as simply being further out.
 */
export const toSphereCageGuides = (
  radius: number,
  segments: number
): Vec3[][] => {
  const ring = (toPoint: (angle: number) => Vec3): Vec3[] =>
    Array.from({ length: segments + 1 }, (_, index) =>
      toPoint((index / segments) * Math.PI * 2)
    );

  return [
    ring(angle => [Math.cos(angle) * radius, Math.sin(angle) * radius, 0]),
    ring(angle => [Math.cos(angle) * radius, 0, Math.sin(angle) * radius]),
    ring(angle => [0, Math.cos(angle) * radius, Math.sin(angle) * radius]),
  ];
};
